import { FormEvent, useCallback, useEffect, useState } from 'react';
import {
  createMainPart,
  deleteMainPart,
  fetchMainParts,
  fetchStockParts,
  updateMainPart,
} from '../../api/manufacturingEquipment';
import {
  EquipmentMainPart,
  EquipmentMainPartPayload,
  ManufacturingStockPart,
} from '../../types/manufacturingEquipment';
import './EquipTabPanel.css';

import { reportError } from '../../utils/notify';
import { formatNumberDisplay, formatNumberInput, parseNumberInput } from '../../utils/numberFormat';
interface Props {
  equipmentId: number;
}

type FormState = {
  mode: 'link' | 'create';
  stockPartId: number | '';
  partName: string;
  spec: string;
  qty: number;
  remark: string;
  initialStockQty: number;
};

const EMPTY: FormState = {
  mode: 'link',
  stockPartId: '',
  partName: '',
  spec: '',
  qty: 1,
  remark: '',
  initialStockQty: 0,
};

function MainPartsTab({ equipmentId }: Props) {
  const [rows, setRows] = useState<EquipmentMainPart[]>([]);
  const [stockParts, setStockParts] = useState<ManufacturingStockPart[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [mainParts, stocks] = await Promise.all([
        fetchMainParts(equipmentId),
        fetchStockParts(),
      ]);
      setRows(mainParts);
      setStockParts(stocks);
      setError('');
    } catch {
      reportError(setError, '주요 부속품을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [equipmentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const linkedStockIds = new Set(rows.map((row) => row.stockPartId).filter(Boolean));
  const availableStock = stockParts.filter(
    (item) => editingId != null || !linkedStockIds.has(item.stockPartId)
  );

  const openCreate = () => {
    setEditingId(null);
    setForm({
      ...EMPTY,
      mode: availableStock.length > 0 ? 'link' : 'create',
    });
    setShowForm(true);
  };

  const openEdit = (row: EquipmentMainPart) => {
    setEditingId(row.partId);
    setForm({
      mode: 'link',
      stockPartId: row.stockPartId ?? '',
      partName: row.partName,
      spec: row.spec ?? '',
      qty: row.qty ?? 0,
      remark: row.remark ?? '',
      initialStockQty: 0,
    });
    setShowForm(true);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      setSaving(true);
      if (editingId == null) {
        const payload: EquipmentMainPartPayload =
          form.mode === 'link'
            ? {
                stockPartId: form.stockPartId === '' ? null : Number(form.stockPartId),
                qty: Number(form.qty) || 0,
                remark: form.remark.trim() || undefined,
              }
            : {
                partName: form.partName.trim(),
                spec: form.spec.trim() || undefined,
                qty: Number(form.qty) || 0,
                remark: form.remark.trim() || undefined,
                initialStockQty: Number(form.initialStockQty) || 0,
              };
        if (form.mode === 'link' && !payload.stockPartId) {
          reportError(setError, '보유 부속품을 선택해 주세요.');
          return;
        }
        if (form.mode === 'create' && !payload.partName) {
          reportError(setError, '품명을 입력해 주세요.');
          return;
        }
        await createMainPart(equipmentId, payload);
      } else {
        await updateMainPart(equipmentId, editingId, {
          qty: Number(form.qty) || 0,
          remark: form.remark.trim() || undefined,
        });
      }
      setShowForm(false);
      await load();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '저장에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: EquipmentMainPart) => {
    if (!window.confirm(`'${row.partName}'을(를) 이 설비의 주요 부속품에서 제거할까요?`)) return;
    try {
      await deleteMainPart(equipmentId, row.partId);
      await load();
    } catch {
      reportError(setError, '삭제에 실패했습니다.');
    }
  };

  return (
    <div className="equip-tab-panel">
      <div className="equip-tab-panel__toolbar">
        <div>
          <h3>주요 부속품</h3>
          <p className="equip-tab-panel__hint">
            이 설비에서 주로 쓰는 부속품입니다. 등록 시 보유 부속품과 연결됩니다.
          </p>
        </div>
        <button type="button" onClick={openCreate}>
          추가
        </button>
      </div>
      {error && <p className="equip-tab-panel__error">{error}</p>}
      {showForm && (
        <form className="equip-tab-panel__form" onSubmit={handleSubmit}>
          {editingId == null && (
            <label className="is-span-2">
              등록 방식
              <select
                value={form.mode}
                onChange={(e) =>
                  setForm((p) => ({ ...p, mode: e.target.value as 'link' | 'create' }))
                }
              >
                <option value="link">보유 부속품에서 선택</option>
                <option value="create">새 부속품 등록 후 연결</option>
              </select>
            </label>
          )}
          {editingId == null && form.mode === 'link' ? (
            <label className="is-span-2">
              보유 부속품 *
              <select
                value={form.stockPartId}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    stockPartId: e.target.value ? Number(e.target.value) : '',
                  }))
                }
                required
              >
                <option value="">선택</option>
                {availableStock.map((item) => (
                  <option key={item.stockPartId} value={item.stockPartId}>
                    {item.partName}
                    {item.spec ? ` (${item.spec})` : ''} · 재고 {item.stockQty}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {editingId == null && form.mode === 'create' ? (
            <>
              <label>
                품명 *
                <input
                  value={form.partName}
                  onChange={(e) => setForm((p) => ({ ...p, partName: e.target.value }))}
                  required
                  placeholder="예: 컷팅날"
                />
              </label>
              <label>
                사양
                <input
                  value={form.spec}
                  onChange={(e) => setForm((p) => ({ ...p, spec: e.target.value }))}
                />
              </label>
              <label>
                초기 보유 수량
                <input
                  type="text"
                  inputMode="decimal"
                  value={formatNumberDisplay(form.initialStockQty)}
                  onChange={(e) => {
                    const parsed = parseNumberInput(formatNumberInput(e.target.value));
                    setForm((p) => ({
                      ...p,
                      initialStockQty: parsed == null ? 0 : Math.max(0, parsed),
                    }));
                  }}
                />
              </label>
            </>
          ) : null}
          <label>
            설비 장착 수량
            <input
              type="text"
              inputMode="decimal"
              value={formatNumberDisplay(form.qty)}
              onChange={(e) => {
                const parsed = parseNumberInput(formatNumberInput(e.target.value));
                setForm((p) => ({
                  ...p,
                  qty: parsed == null ? 0 : Math.max(0, parsed),
                }));
              }}
            />
          </label>
          <label>
            비고
            <input
              value={form.remark}
              onChange={(e) => setForm((p) => ({ ...p, remark: e.target.value }))}
            />
          </label>
          <div className="equip-tab-panel__form-actions">
            <button type="button" className="is-ghost" onClick={() => setShowForm(false)}>
              취소
            </button>
            <button type="submit" disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>
      )}
      {loading ? (
        <p className="equip-tab-panel__hint">불러오는 중...</p>
      ) : rows.length === 0 ? (
        <p className="equip-tab-panel__hint">이 설비의 주요 부속품을 등록해 주세요.</p>
      ) : (
        <table className="equip-tab-panel__table">
          <thead>
            <tr>
              <th style={{ width: '48px' }}>No</th>
              <th>품명</th>
              <th>사양</th>
              <th style={{ width: '80px' }}>장착수량</th>
              <th style={{ width: '80px' }}>보유재고</th>
              <th>비고</th>
              <th style={{ width: '120px' }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.partId}>
                <td>{index + 1}</td>
                <td>{row.partName}</td>
                <td>{row.spec || '-'}</td>
                <td>{row.qty ?? 0}</td>
                <td>{row.stockQty ?? '-'}</td>
                <td>{row.remark || '-'}</td>
                <td>
                  <div className="equip-tab-panel__row-actions">
                    <button type="button" className="is-ghost" onClick={() => openEdit(row)}>
                      수정
                    </button>
                    <button type="button" className="is-danger" onClick={() => void handleDelete(row)}>
                      삭제
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default MainPartsTab;
